import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrayProvider, useArrayContext } from "../../../state/ArrayContext";
import { findFirstComponentOfType } from "../../internal/TextExtractor";
import { SourceItem } from "../../../shared/models/source-item.model";

interface SortArrayProps {
    order?: "ascending" | "descending";
    children: React.ReactNode;
}

/***
 * Array transformer that sorts an array of SourceItems, based on the value field in the SourceItem.
 * 
 * Params:
 *    order: the order (ascending or descending)
 */
export const SortArray = React.memo(function SortArray({ order, children }: SortArrayProps): React.ReactElement {
    const { setArray } = useArrayContext();

    // Get the source child which should contain an array source with values to sort
    const sourceChild = useMemo(() => findFirstComponentOfType(children, "arg-source"), [children]);
    const [sourceArray, setSourceArray] = useState<Array<SourceItem>>();
    const memoizedSourceArray = useMemo(() => sourceArray, [sourceArray]);

    const reverseOrder: boolean = useMemo(() => !order || order === "ascending" ? false : true, [order]);

    const compare = useCallback((a: SourceItem, b: SourceItem): number => {
        if (a.value === b.value) {
            return 0;
        }
        else if (a.value === null) {
            return reverseOrder ? 1 : -1;
        }
        else if (b.value === null) {
            return reverseOrder ? -1 : 1;
        }
        else if (typeof a.value === "number" && typeof b.value === "number") {
            return reverseOrder ? b.value - a.value : a.value - b.value;
        }
        else {
            return reverseOrder ? b.value.toString().localeCompare(a.value.toString()) : a.value.toString().localeCompare(b.value.toString());
        }
    }, [reverseOrder]);

    useEffect(() => {
        if (memoizedSourceArray) {
            const items = memoizedSourceArray
                .sort((a, b) => compare(a, b))
                .map((item, ix) => ({ ...item, position: ix + 1 }));
            setArray(items);
        }
    }, [memoizedSourceArray, setArray, compare]);

    return <>
        {useMemo(() => {
            return <ArrayProvider setArray={setSourceArray}>
                {sourceChild?.props.children}
            </ArrayProvider>
        }, [sourceChild, setSourceArray])}
    </>;
});